import { ceil } from "lodash";
import React, { useCallback, useEffect } from "react";
import { useIdleTimer } from "react-idle-timer";
import { AxiosApiWrapper } from "../services/api/AxiosApiWrapper";

const api = new AxiosApiWrapper();
const SHOW_PROMPT_BEFORE_IDLE_TIME = 30;
const SESSION_TIMEOUT_LIMIT = 2147483647 - 1;
const SESSION_INACTIVITY_TIMEOUT_KEY = "tcbase_sessionInactivityTimeout";
const SESSION_INACTIVITY_TIMEOUT_MESSAGE_KEY =
  "tcbase_sessionInactivityTimeout_resetTimer";

/**
 * Hook to handle session inactivity timeout
 * @param sessionTimeoutSeconds - session timeout in seconds (max 2147483647 - 1)
 * @param enabled - enable/disable the timeout
 * @param promptBeforeIdle - time in seconds before the timeout to prompt the user (default 30, max sessionTimeoutSeconds)
 */
const useSessionInactivityTimeout = (
  sessionTimeoutSeconds: number,
  enabled = true,
  promptBeforeIdle = SHOW_PROMPT_BEFORE_IDLE_TIME
) => {
  const sessionTimeoutMilliseconds = Math.min(
    sessionTimeoutSeconds * 1000,
    SESSION_TIMEOUT_LIMIT
  );

  const limitedPromptBeforeIdle = Math.min(
    promptBeforeIdle,
    sessionTimeoutMilliseconds / 1000
  );

  const [remainingTime, setRemainingTime] = React.useState(
    limitedPromptBeforeIdle
  );
  const [isPrompting, setIsPrompting] = React.useState(false);

  const onPrompt = () => {
    setIsPrompting(true);
    setRemainingTime(limitedPromptBeforeIdle);
  };

  const onTimeout = useCallback(
    () => api.logout({ next: window.location.href }),
    []
  );

  const onMessage = (data: string) => {
    if (data === SESSION_INACTIVITY_TIMEOUT_MESSAGE_KEY) setIsPrompting(false);
  };

  const { getRemainingTime, activate, message } = useIdleTimer({
    timeout: sessionTimeoutMilliseconds,
    onIdle: onTimeout,
    onPrompt,
    disabled: !enabled,
    promptBeforeIdle: limitedPromptBeforeIdle * 1000,
    crossTab: true,
    syncTimers: 100,
    name: SESSION_INACTIVITY_TIMEOUT_KEY,
    onMessage
  });

  const onReset = useCallback(() => {
    activate();
    setIsPrompting(false);
    setRemainingTime(limitedPromptBeforeIdle);
    message(SESSION_INACTIVITY_TIMEOUT_MESSAGE_KEY, false);
  }, [activate, limitedPromptBeforeIdle, message]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!enabled) return;
      setRemainingTime(ceil(getRemainingTime() / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [enabled, getRemainingTime]);

  return {
    remainingTime,
    timeoutHandler: onTimeout,
    resetHandler: onReset,
    isPrompting
  };
};

export default useSessionInactivityTimeout;
